import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/property/PropertyCard";
import FindYourStayCTA from "@/components/common/FindYourStayCTA";
import StructuredData, { breadcrumbSchema, orgSchema } from "@/components/common/StructuredData";
import useSiteConfig from "@/lib/useSiteConfig";

/**
 * SeoLandingPage — editorial long-tail SEO landing page template.
 *
 * Each route under /app/frontend/src/pages/SeoPages.jsx renders this
 * template with its own title, meta description, hero copy, intro
 * paragraphs and a property filter (collection / location / featured).
 * The template fetches the matching properties from the API and
 * surfaces them as PropertyCards alongside crawlable editorial body
 * copy — so the page has unique HTML for SEO, not a content-empty SPA
 * route.
 */
export default function SeoLandingPage({
  pageId,
  title,
  metaDescription,
  canonicalPath,
  eyebrow,
  heading,
  scriptHeading,
  intro,
  body = [],
  filter = {},
  ctaCopy,
  breadcrumbs,
}) {
  const cfg = useSiteConfig();
  const [properties, setProperties] = useState([]);

  useEffect(() => {
    api.get("/properties", { params: filter }).then(({ data }) => setProperties(data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  return (
    <div data-testid={`seo-page-${pageId}`} className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-10">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={`https://vyagram.com${canonicalPath}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={`https://vyagram.com${canonicalPath}`} />
        {properties[0]?.hero_image && <meta property="og:image" content={properties[0].hero_image} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <StructuredData
        id={`seo-schema-${pageId}`}
        data={[
          orgSchema({
            phone: cfg.whatsapp_number ? `+${cfg.whatsapp_number}` : undefined,
            email: cfg.support_email,
          }),
          breadcrumbSchema(breadcrumbs || [
            { name: "Home", url: "https://vyagram.com/" },
            { name: heading, url: `https://vyagram.com${canonicalPath}` },
          ]),
        ]}
      />

      <header className="max-w-3xl">
        {eyebrow && <p className="vyg-eyebrow">{eyebrow}</p>}
        <h1 className="mt-3 font-display text-4xl text-noir sm:text-5xl lg:text-6xl">
          {heading}{" "}
          {scriptHeading && <span className="font-script text-gold">{scriptHeading}</span>}
        </h1>
        {intro && <p className="editorial-body mt-5 !text-lg text-noir/80">{intro}</p>}
      </header>

      {body.length > 0 && (
        <section className="mt-12 grid gap-6 lg:grid-cols-2 lg:gap-10">
          {body.map((b) => (
            <article key={b.title} className="rounded-2xl border border-forest/10 bg-white p-6 shadow-card sm:p-8">
              <h2 className="font-display text-2xl text-forest">{b.title}</h2>
              <p className="editorial-body mt-3 !text-base text-noir/75">{b.body}</p>
            </article>
          ))}
        </section>
      )}

      {properties.length > 0 && (
        <section className="mt-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h2 className="font-display text-3xl text-noir sm:text-4xl">Stays that fit</h2>
            <Link to="/stays" className="vyg-link-gold">See the full portfolio <ArrowRight size={14} /></Link>
          </div>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {properties.slice(0, 9).map((p) => (<PropertyCard key={p.slug} p={p} />))}
          </div>
        </section>
      )}

      <FindYourStayCTA
        eyebrow={ctaCopy?.eyebrow || "Plan your stay"}
        title={ctaCopy?.title || "Tell us a little — we'll take it from there"}
        subtitle={ctaCopy?.subtitle}
      />
    </div>
  );
}
